import { useContext, useRef, useState } from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { UserContext } from '../../../../context/UserContext';
import { ActionRow, Section, TableRow, TextRow } from '../SettingsRows';

export function AccountTab(): React.JSX.Element {
    const { user, updateUser, uploadAvatar } = useContext(UserContext);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setAvatarUploading(true);
        const result = await uploadAvatar(file);
        setAvatarUploading(false);
        setAvatarError(result.ok ? null : result.error ?? 'Could not upload that image.');
    };

    const [displayName, setDisplayName] = useState(user?.firstName ?? '');
    const [username, setUsername] = useState(user?.username ?? '');
    const [email, setEmail] = useState(user?.email ?? '');
    const [error, setError] = useState<string | null>(null);

    const [savedDisplayName, setSavedDisplayName] = useState(user?.firstName ?? '');
    const [savedUsername, setSavedUsername] = useState(user?.username ?? '');
    const [savedEmail, setSavedEmail] = useState(user?.email ?? '');

    const [passwordEditing, setPasswordEditing] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const commit = async (field: 'displayName' | 'username' | 'email', value: string, savedValue: string, setSaved: (v: string) => void) => {
        if (value === savedValue) return;
        const result = await updateUser({ [field]: value });
        if (result.ok) {
            setError(null);
            setSaved(value);
        } else {
            setError(result.error ?? 'Could not save that change.');
        }
    };

    const handleSavePassword = async () => {
        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match.');
            return;
        }
        const result = await updateUser({ newPassword });
        if (result.ok) {
            setPasswordError(null);
            setPasswordEditing(false);
            setNewPassword('');
            setConfirmPassword('');
        } else {
            setPasswordError(result.error ?? 'Could not change your password.');
        }
    };

    return (
        <>
            <Section title="Identity">
                <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    ref={avatarInputRef}
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                />
                <ActionRow
                    label="Profile picture"
                    desc={avatarError ?? 'JPEG, PNG or WebP, up to 5MB.'}
                    actionLabel={avatarUploading ? 'Uploading…' : 'Change'}
                    onClick={() => !avatarUploading && avatarInputRef.current?.click()}
                />
                <TextRow
                    label="Display name"
                    desc="Shown on shared devices and in the queue."
                    value={displayName}
                    onChange={(v) => {
                        setDisplayName(v);
                        setError(null);
                    }}
                    onBlur={() => commit('displayName', displayName, savedDisplayName, setSavedDisplayName)}
                />
                <TextRow
                    label="Username"
                    desc="Used to sign in."
                    value={username}
                    onChange={(v) => {
                        setUsername(v);
                        setError(null);
                    }}
                    onBlur={() => commit('username', username, savedUsername, setSavedUsername)}
                    error={error ?? undefined}
                />
                <TextRow
                    label="Email"
                    desc="For password resets only, never sent anywhere."
                    value={email}
                    onChange={(v) => {
                        setEmail(v);
                        setError(null);
                    }}
                    onBlur={() => commit('email', email, savedEmail, setSavedEmail)}
                />
                {!passwordEditing ? (
                    <ActionRow
                        label="Password"
                        desc="Last changed 4 months ago."
                        actionLabel="Change"
                        onClick={() => setPasswordEditing(true)}
                    />
                ) : (
                    <>
                        <TextRow
                            label="New password"
                            desc={passwordError ?? 'At least 8 characters.'}
                            value={newPassword}
                            onChange={setNewPassword}
                            type="password"
                        />
                        <TextRow
                            label="Confirm password"
                            desc="Enter the new password again."
                            value={confirmPassword}
                            onChange={setConfirmPassword}
                            type="password"
                        />
                        <ActionRow
                            label="Save password"
                            desc="Applies immediately, on this device and any other."
                            actionLabel="Save"
                            tone="solid"
                            onClick={handleSavePassword}
                        />
                        <ActionRow
                            label="Cancel"
                            desc="Discard this change."
                            actionLabel="Cancel"
                            onClick={() => {
                                setPasswordEditing(false);
                                setNewPassword('');
                                setConfirmPassword('');
                                setPasswordError(null);
                            }}
                        />
                    </>
                )}
            </Section>
            <Section title="Signed in devices">
                <TableRow icon={Monitor} main="This browser" sub="Firefox on Linux · active now" tag="CURRENT" tagTone="blue" actionLabel="Sign out" />
                <TableRow icon={Smartphone} main="Pixel 8" sub="Android app · Aug 21" actionLabel="Sign out" />
                <TableRow icon={Tablet} main="Kitchen tablet" sub="Web · Aug 12" actionLabel="Sign out" />
            </Section>
        </>
    );
}
